import React, {useEffect, useRef, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import ReactSelect from 'react-select/async';

import {LoadingIndicator} from '../components/LoadingIndicator';
import {Flow, FlowFetchResponse} from '../_services/Flow';
import {NavUserMaintenance} from '../components/NavUserMaintenance';
import {User} from '../_services/User';
import {Form as FormType} from '../types';

type RoleMaintenancePageProps = {
    pageTitle?: string;
    hasSponsorField?: boolean;
};

type UserState = {
    isEditing: boolean;
    isSaving: boolean;
    isDeleting: boolean;
    formState: any;
};

const initialUserState: UserState = {
    isEditing: false,
    isSaving: false,
    isDeleting: false,
    formState: {}
};

/**
 * This page was thrown together and needs quite a bit of cleanup....
 *
 * @constructor
 */
export const RoleMaintenance: React.FunctionComponent<RoleMaintenancePageProps> = ({
                                                                                       pageTitle,
                                                                                       hasSponsorField = false
                                                                                   }) => {
    const {workflowId, roleName} = useParams();
    const history = useHistory();
    const userInputRef = useRef(null);
    const userSponsorInputRef = useRef(null);

    const [form, setForm] = useState<FormType>();
    const [pageIsLoading, setPageIsLoading]: any = useState(false);
    const [errorMessage, setErrorMessage]: any = useState('');

    const [selectedRole, setSelectedRole] = useState<any>(null);
    const [users, setUsers] = useState<any>([]);
    const [filterText, setFilterText] = useState<string>('');
    const [newUserState, setNewUserState] = useState<any>({});
    const [isSavingNewUser, setIsSavingNewUser] = useState<boolean>(false);
    const [userStateMap, setUserStateMap] = useState<any>({});

    const [roles, setRoles] = useState<any>([]);

    useEffect(() => {
        Flow.getInstance().getForm(workflowId)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setForm(response.json.form);
                } else {
                    throw Error(response?.json?.message || 'Error fetching Workflow');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error fetching Workflow');
            })
            .finally(() => {
            });
    }, [workflowId]);

    useEffect(() => {
        if (form) {
            if (!(User.getInstance().isIsWfo() || (form?.flow_form_admin_role && User.getInstance().hasRole(form.flow_form_admin_role)))) {
                console.log('redirecting');
                history.replace('/');
            }
        }
    }, [form]);

    useEffect(() => {
        setSelectedRole(roleName ? {value: roleName, label: roleName} : null);
    }, [workflowId, roleName]);

    useEffect(() => {
        if (!selectedRole) return;
        if (!workflowId) return;

        setPageIsLoading(true);
        setErrorMessage('');
        setUsers([]);
        setFilterText('');

        Flow.getInstance().getUsersForRole(workflowId, selectedRole.value)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setUsers(response.json.users);
                } else {
                    throw Error(response?.json?.message || 'Error fetching Assignees');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error fetching Assignees');
            })
            .finally(() => {
                setPageIsLoading(false);
            });
    }, [selectedRole]);

    const getUserState = (userId: string): UserState => {
        return userStateMap.hasOwnProperty(userId) ? userStateMap[userId] : {...initialUserState};
    }

    const setUserState = (userId: string, userState: any): void => {
        setUserStateMap((state: any) => ({
            ...state,
            [userId]: {
                ...(state.hasOwnProperty(userId) ? state[userId] : {...initialUserState}),
                ...userState
            }
        }))
    }

    /**
     * Get available roles for a passed workflowId
     * @param workflowId
     */
    const handleOptionsRoles = (workflowId: string) => (inputValue: string) => {
        if (roles?.length > 0) {
            return new Promise((resolve, reject) => {
                resolve(roles.filter((role: any) => !inputValue || role.label.toLowerCase().includes(inputValue.toLowerCase())));
            });
        }
        return Flow.getInstance().getFormRoles(workflowId)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setRoles(response.json.roles.map((role: any) => ({
                        label: role.displayName,
                        value: role.name,
                        ...role
                    })));
                    return response.json.roles
                        .map((role: any) => ({
                            label: role.displayName,
                            value: role.name,
                            ...role
                        }))
                        .filter((role: any) => !inputValue || role.label.toLowerCase().includes(inputValue.toLowerCase()));
                } else {
                    throw Error(response?.json?.mesasge || 'Error fetching roles');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error fetching roles');
            })
            .finally(() => {

            })
    };

    /**
     * Change Event Handler for Assignee Step dropdown
     * @param option
     */
    const handleChangeRole = (option: any) => {
        setSelectedRole(option);
        setUsers([]);
    };

    /**
     * Given a string of text to use as a filter, return a function to be passed to Array.filter
     * This function filters the assignee table
     * @param filter
     */
    const filterUsers = (filter: string) => (user: any) => {
        const filterRegexp = new RegExp(filter.toLowerCase());

        return (!filter || (filter && (
            filterRegexp.test(user.user_email.toLowerCase())
        )))
    };

    const handleUserDelete = (roleName: string, user: any) => {
        setUserState(user.id, {
            isDeleting: true
        });
        setErrorMessage('');
        Flow.getInstance().deleteRoleFromUser(workflowId, roleName, user.user_email)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setUsers((state: any) => {
                        const idx = state.findIndex((a: any) => a.user_email === user.user_email);
                        return [
                            ...state.slice(0, idx),
                            ...state.slice(idx + 1)
                        ]
                    });
                } else {
                    throw Error(response?.json?.message || 'Error deleting');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error deleting');
            })
            .finally(() => {
                setUserState(user.id, {...initialUserState});
            });
    };

    const handleUserAdd = (roleName: string, userEmail: any, sponsor: any = '') => {
        setErrorMessage('');
        setIsSavingNewUser(true);
        Flow.getInstance().addRoleToUser(workflowId, roleName, userEmail, sponsor)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setUsers((state: any) => {
                        return [
                            ...(state ? state : []),
                            {...response.json.user}
                        ]
                    });
                    setNewUserState(() => ({}));
                } else {
                    throw Error(response?.json?.message || 'Error adding user(s)');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error adding user(s)');
            })
            .finally(() => {
                setIsSavingNewUser(false);
            });
    };

    const handleSave = (userIndex: number, roleName: string, user: any, values: any) => {
        setErrorMessage('');
        setUserState(user.id, {
            isSaving: true
        });
        Flow.getInstance().updateUserAttributesForRole(workflowId, roleName, user.user_email, values.value)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    // update user record
                    setUsers((state: any) => ([
                        ...state.splice(0, userIndex),
                        {
                          ...user,
                          sponsor: values.value
                        },
                        ...state.splice(userIndex + 1),
                    ]));
                } else {
                    throw Error(response?.json?.message);
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error saving');
            })
            .finally(() => {
                setUserState(user.id, {...initialUserState});
            });
    }

    /**
     * When pasting formatted text (fields tab-delimited, lines CR/LF-delimited), this function handles
     * parsing and saving the lines
     * @param pastedValue
     */
    const handlePaste = (pastedValue: string) => {
        const lines = pastedValue.split(/\r?\n/g);
        lines.forEach((value: string) => {
            const parts = value.split(/\t/);
            const userEmail = parts.length > 0 ? parts[0] : '';
            const sponsor = parts.length > 1 ? parts[1] : '';
            if (userEmail) {
                handleUserAdd(selectedRole.value, userEmail, sponsor);
            }
        });

        setNewUserState(() => ({}));
    };

    if (!form) {
        return <LoadingIndicator/>;
    }

    return (
        <div>
            <div className="col-12">
                {form && <h1 className={'abb-header'}>{form.title}</h1>}
                <h2>{pageTitle ? pageTitle : 'Workflow Role User Maintenance'}</h2>
            </div>
            {workflowId && <NavUserMaintenance workflowId={workflowId}/>}
            {errorMessage && <p className="alert alert-danger">{errorMessage}</p>}
            {pageIsLoading && <LoadingIndicator/>}

            {!roleName &&
                <div className="row">
                    <div className="col-4 p-4">
                        <ReactSelect
                            placeholder="Select a Workflow Role..."
                            required={true}
                            isClearable={true}
                            loadOptions={handleOptionsRoles(workflowId as string)}
                            defaultOptions
                            value={selectedRole}
                            onChange={handleChangeRole}
                        />
                    </div>
                </div>
            }
            {selectedRole?.description && <div className={'mt-2 mb-4'}>{selectedRole?.description}</div>}
            {!roleName && !selectedRole && <p><em>Select a Workflow Role to List Users</em></p>}

            {selectedRole &&
                <div className="row mt-2">
                    <div className="col-3">
                        <input type="text"
                               className="form-control"
                               placeholder="Filter Users...."
                               value={filterText}
                               onChange={(e) => {
                                   setFilterText(e.target.value)
                               }}
                        />
                    </div>
                </div>
            }

            {selectedRole &&
                <div className="row mt-2">
                    <div className="col-12">
                        <table className="table table-sm">
                            <thead>
                            <tr>
                                <th>User Email</th>
                                {hasSponsorField && <th>ABB Sponsor Email</th>}
                                <th style={{width: '100px', whiteSpace: 'nowrap'}}/>
                            </tr>
                            </thead>
                            <tbody>
                            {users.filter(filterUsers(filterText)).map((user: any, index: number) => (
                                <tr key={user.id}>
                                    <td>
                                        {user.user_email}
                                    </td>
                                    {hasSponsorField &&
                                        <td>
                                            {getUserState(user.id).isEditing ?
                                                <>
                                                    <input type="text"
                                                           className="form-control"
                                                           disabled={getUserState(user.id).isSaving}
                                                           value={getUserState(user.id).formState?.value || ''}
                                                           onChange={(e) => {
                                                               setUserState(user.id, {formState: {value: e.target.value}});
                                                           }}
                                                    />
                                                </>
                                                :
                                                <>{user?.sponsor || ''}</>
                                            }
                                        </td>
                                    }
                                    <td style={{whiteSpace: 'nowrap'}}>
                                        {getUserState(user.id).isEditing ?
                                            <>
                                                {getUserState(user.id).isSaving ?
                                                    <i className="fas fa-spinner fa-spin"/>
                                                    :
                                                    <button type="button"
                                                            className="btn btn-link btn-sm text-success"
                                                            onClick={() => handleSave(index, selectedRole.value, user, getUserState(user.id).formState)}
                                                    >Save
                                                    </button>
                                                }
                                                <button type="button"
                                                        className="btn btn-link btn-sm"
                                                        disabled={getUserState(user.id).isSaving}
                                                        onClick={() => {
                                                            setUserState(user.id, {...initialUserState});
                                                        }}
                                                >Cancel
                                                </button>
                                            </>
                                            :
                                            <>
                                                {hasSponsorField &&
                                                    <button type="button"
                                                            className="btn btn-link btn-sm"
                                                            onClick={() => {
                                                                setUserState(user.id, {
                                                                    ...initialUserState,
                                                                    isEditing: true,
                                                                    formState: {
                                                                        value: user?.sponsor || ''
                                                                    }
                                                                })
                                                            }}
                                                    >Edit</button>
                                                }
                                                {getUserState(user.id).isDeleting ?
                                                    <i className="fas fa-spinner fa-spin"/>
                                                    :
                                                    <button type="button"
                                                            className="btn btn-link btn-sm text-danger"
                                                            onClick={() => {
                                                                handleUserDelete(selectedRole.value, user);
                                                            }}
                                                    >Delete
                                                    </button>
                                                }
                                            </>
                                        }
                                    </td>
                                </tr>
                            ))}
                            <tr>
                                <td>
                                <textarea rows={1}
                                          className="form-control"
                                          style={{height: 'calc(1.8em + .75rem + 2px)'}}
                                          disabled={isSavingNewUser}
                                          ref={userInputRef}
                                          value={newUserState?.userEmail || ''}
                                          onChange={(e) => {
                                              if (newUserState?.isPasting) {
                                                  handlePaste(e.target.value);
                                              } else {
                                                  const value = e.target.value;
                                                  setNewUserState((prevState: any) => ({
                                                      ...prevState,
                                                      userEmail: value
                                                  }))
                                              }
                                          }}
                                          onPaste={() => {
                                              setNewUserState((prevState: any) => ({
                                                  ...prevState,
                                                  isPasting: true
                                              }));
                                          }}
                                          onKeyDown={(e) => {
                                              if (e.keyCode === 13) {
                                                  if (userSponsorInputRef && userSponsorInputRef?.current) {
                                                      // @ts-ignore
                                                      userSponsorInputRef.current.focus();
                                                  } else {
                                                      handleUserAdd(selectedRole.value, newUserState.userEmail, newUserState.sponsor);
                                                  }
                                              }
                                          }}
                                />
                                </td>
                                {hasSponsorField &&
                                    <td>
                                        <input type="text"
                                               className="form-control"
                                               ref={userSponsorInputRef}
                                               disabled={isSavingNewUser}
                                               value={newUserState?.sponsor || ''}
                                               onChange={(e) => {
                                                   const value = e.target.value;
                                                   setNewUserState((state: any) => ({
                                                       ...state,
                                                       sponsor: value
                                                   }));
                                               }}
                                               onKeyDown={(e) => {
                                                   if (e.keyCode === 13) {
                                                       handleUserAdd(selectedRole.value, newUserState.userEmail, newUserState.sponsor);
                                                   }
                                               }}
                                        />
                                    </td>
                                }
                                <td>
                                    {isSavingNewUser ?
                                        <i className="fas fa-spinner fa-spin"/>
                                        :
                                        <button type="button"
                                                className="btn btn-link btn-sm"
                                                onClick={() => {
                                                    handleUserAdd(selectedRole.value, newUserState.userEmail, newUserState.sponsor);
                                                }}
                                        >Add
                                        </button>
                                    }
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            }
        </div>
    );
};